import { message } from 'antd'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { produce } from 'immer'
import { sortBy } from 'lodash-es'
import { shallow } from 'zustand/shallow'
import { createWithEqualityFn } from 'zustand/traditional'
import { API, isAxiosError } from '../../lib/api'
import { useUserStore } from '../user'
import { RoleState } from './types'

const dataExpirationMinutes = 5

export const useUsersStore = createWithEqualityFn<RoleState>(
  (set, get) => ({
    usersExpiration: Date.now(),
    users: [],
    getUsers: async () => {
      const { usersExpiration } = get()
      if (usersExpiration > Date.now()) return
      set({ usersExpiration: Date.now() + dayjs.duration({ minutes: dataExpirationMinutes }).asMilliseconds() })

      const users = await API.getUsers()
      if (users) {
        set({ users })
      }
    },
    createUser: async (user) => {
      const newUser = await API.createUser(user)
      const { users } = get()

      if (newUser) {
        const index = users.findIndex((user) => user.id === newUser.id)
        if (index === -1) {
          set(
            produce<RoleState>((state) => {
              state.users.push(newUser)
              state.users = sortBy(state.users, 'lastName')
            }),
          )
          message.success(i18next.t('message.userUpdate'))
        } else {
          set(
            produce<RoleState>((state) => {
              state.users[index] = newUser
            }),
          )
          message.success(i18next.t('message.userCreate'))
        }
      }
    },
    updateUser: async (id, user) => {
      const response = await API.updateUser(id, user)

      if (isAxiosError(response)) {
        if (response.response?.status === 403) {
          message.error({ content: i18next.t('message.permissionDenied') })
        }
        return
      }

      if (response) {
        set((prevState) => ({
          users: prevState.users.map((prevUser) => (prevUser.id === response.id ? response : prevUser)),
        }))
        const ownId = useUserStore.getState().userId
        if (response.id === ownId) await API.refreshToken()
        message.success(i18next.t('message.userUpdate'))
      }
    },
    updateUserRoles: async (id, roles) => {
      const response = await API.updateUserRoles(id, roles)

      if (isAxiosError(response)) {
        if (response.response?.status === 403) {
          message.error({ content: i18next.t('message.permissionDenied') })
        }
        return
      }

      if (response) {
        set((prevState) => ({
          users: prevState.users.map((prevUser) => (prevUser.id === id ? { ...prevUser, roles } : prevUser)),
        }))
      }
    },
    reset: () => set({ users: [], usersExpiration: Date.now() }),
  }),
  shallow,
)
